import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronRight,
  GripVertical,
  Home,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgResidentialRooms, type RoomInput } from '../../hooks/useResidentialRooms';
import { useOrgInventory } from '../../hooks/useOrgInventory';
import { useOrgSmartRoomProfiles } from '../../hooks/useOrgSmartRoomProfiles';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { OrgAutoAddRulesTab } from '../../components/admin/AutoAddRulesTab';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SearchableDropdown } from '../../components/ui/CustomDropdown';
import {
  OrgResidentialRoom,
  RoomProfile,
  RoomProfileItem,
  RoomProfileLevel,
  SmartProfileLevel,
  SmartRoomProfileConfig,
  ResidentialPropertyType,
} from '../../types/admin';

// ── Constants ──────────────────────────────────────────────────────────────

const PROFILE_LEVELS: RoomProfileLevel[] = ['minimal', 'standard', 'heavy', 'full'];

const LEVEL_LABELS: Record<RoomProfileLevel, string> = {
  minimal: 'Minimal',
  standard: 'Standard',
  heavy: 'Heavy',
  full: 'Full',
};

// ── Label component ────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-bold uppercase tracking-widest text-slate"
    >
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && (
        <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
      )}
    </label>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function emptyProfile(level: RoomProfileLevel): RoomProfile {
  return { level, items: [] };
}

function ensureAllProfiles(profiles: RoomProfile[]): RoomProfile[] {
  return PROFILE_LEVELS.map(
    level => profiles.find(p => p.level === level) ?? emptyProfile(level),
  );
}

// ── Room Form ──────────────────────────────────────────────────────────────

interface RoomFormProps {
  initial?: OrgResidentialRoom;
  orgId: string;
  onClose: () => void;
  onSave: (input: RoomInput) => Promise<unknown>;
}

function RoomForm({ initial, orgId, onClose, onSave }: RoomFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [profiles, setProfiles] = useState<RoomProfile[]>(
    ensureAllProfiles(initial?.profiles ?? []),
  );
  const [activeLevel, setActiveLevel] = useState<RoomProfileLevel>('standard');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { items: inventoryItems, loading: inventoryLoading } = useOrgInventory(orgId);

  // Build dropdown options from inventory items (residential only, sorted by name)
  const itemOptions = inventoryItems
    .filter(i => i.quoteType === 'residential')
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(i => ({ value: i.id, label: i.name }));

  const activeProfile = profiles.find(p => p.level === activeLevel)!;

  function updateActiveProfile(updated: Partial<RoomProfile>) {
    setProfiles(prev =>
      prev.map(p => (p.level === activeLevel ? { ...p, ...updated } : p)),
    );
  }

  function updateItem(index: number, field: keyof RoomProfileItem, value: string | number) {
    const newItems = [...activeProfile.items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateActiveProfile({ items: newItems });
  }

  function selectItem(index: number, itemId: string) {
    const inv = inventoryItems.find(i => i.id === itemId);
    if (!inv) return;
    const newItems = [...activeProfile.items];
    newItems[index] = { ...newItems[index], itemId: inv.id, itemName: inv.name };

    // Auto-add equipment items defined on this inventory item
    if (inv.addEquipment && inv.addEquipment.length > 0) {
      for (const eq of inv.addEquipment) {
        const already = newItems.some(i => i.itemId === eq.itemId);
        if (!already) {
          newItems.push({ itemId: eq.itemId, itemName: eq.itemName, quantity: eq.quantity });
        }
      }
    }

    updateActiveProfile({ items: newItems });
  }

  function addItem() {
    updateActiveProfile({
      items: [...activeProfile.items, { itemId: '', itemName: '', quantity: 1 }],
    });
  }

  function removeItem(index: number) {
    updateActiveProfile({
      items: activeProfile.items.filter((_, i) => i !== index),
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Room name is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({ name: name.trim(), profiles });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save room.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[80vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Room' : 'Add Room'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Room name */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="room-name">Room Name</FieldLabel>
            <input
              id="room-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Living Room"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Profile level tabs */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="room-profiles">Profiles</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {PROFILE_LEVELS.map(level => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setActiveLevel(level)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${activeLevel === level
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {LEVEL_LABELS[level]}
                </button>
              ))}
            </div>
          </div>

          {/* Active profile content */}
          <div className="flex flex-col gap-3 p-3 bg-mist/50 rounded-xl border border-stone/50">
            {/* Average square feet */}
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor={`sqft-${activeLevel}`} optional>
                Average Square Feet
              </FieldLabel>
              <input
                id={`sqft-${activeLevel}`}
                type="number"
                min={0}
                value={activeProfile.averageSquareFeet ?? ''}
                onChange={e =>
                  updateActiveProfile({
                    averageSquareFeet: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                placeholder="e.g. 250"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>

            {/* Items header */}
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                Items
                <span className="font-normal normal-case tracking-normal ml-1">
                  ({activeProfile.items.length})
                </span>
              </p>
              <button
                type="button"
                onClick={addItem}
                className="h-8 px-3 rounded-lg border border-stone bg-white
                           flex items-center gap-1.5
                           text-[12px] font-semibold text-slate
                           hover:border-plum hover:text-plum active:bg-mist
                           transition-colors duration-150"
              >
                <Plus size={13} />
                Add Item
              </button>
            </div>

            {/* Items list — grouped by residential type */}
            {inventoryLoading && (
              <div className="flex justify-center py-3">
                <LoadingSpinner size="sm" />
              </div>
            )}
            {activeProfile.items.length === 0 && !inventoryLoading && (
              <p className="text-[13px] text-slate py-2">No items yet.</p>
            )}
            {(() => {
              // Group items by residential type, preserving original indices
              const indexed = activeProfile.items.map((item, idx) => {
                const inv = inventoryItems.find(i => i.id === item.itemId);
                return { item, idx, resType: inv?.residentialType };
              });
              const customerItems = indexed.filter(i => !i.resType || i.resType === 'customer_item');
              const packingItems = indexed.filter(i => i.resType === 'packing_item');
              const equipmentItems = indexed.filter(i => i.resType === 'equipment');
              const sections = [
                { label: 'Customer Items', items: customerItems },
                { label: 'Packing Items', items: packingItems },
                { label: 'Equipment', items: equipmentItems },
              ].filter(s => s.items.length > 0);

              return sections.map(section => (
                <div key={section.label} className="flex flex-col gap-2">
                  {sections.length > 1 && (
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate/70 mt-1">
                      {section.label}
                    </p>
                  )}
                  {section.items.map(({ item, idx }) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 p-3 bg-white rounded-lg border border-stone"
                    >
                      <div className="flex-1 flex flex-col gap-2">
                        <SearchableDropdown
                          options={itemOptions}
                          value={item.itemId}
                          onChange={v => selectItem(idx, v)}
                          placeholder="Select item…"
                        />
                        <div className="flex items-center gap-2">
                          <p className="flex-1 text-[11px] font-bold uppercase tracking-widest text-slate">
                            Qty
                          </p>
                          <input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={e => updateItem(idx, 'quantity', Number(e.target.value) || 1)}
                            className="w-20 h-9 px-3 rounded-lg border border-stone bg-white
                                       text-[13px] text-midnight text-center
                                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                       transition-colors duration-150"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg
                                   text-slate hover:text-rose hover:bg-rose/10
                                   transition-colors duration-150"
                        aria-label="Remove item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving...' : initial ? 'Save Changes' : 'Add Room'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Restore Result Banner ──────────────────────────────────────────────────

interface RestoreResultBannerProps {
  added: number;
  updated: number;
  onClose: () => void;
}

function RestoreResultBanner({ added, updated, onClose }: RestoreResultBannerProps) {
  const parts: string[] = [];
  if (added > 0) parts.push(`${added} added`);
  if (updated > 0) parts.push(`${updated} updated`);
  const summary = parts.length > 0 ? parts.join(', ') : 'no changes';

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
      <div className="flex-1 min-w-0">
        <p className="text-small font-semibold text-midnight">
          Restore complete — {summary}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 w-6 h-6 flex items-center justify-center text-slate
                   hover:text-midnight transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Source Badge ────────────────────────────────────────────────────────────

function SourceBadge({ source }: { source: 'system' | 'org' }) {
  return (
    <span
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
        ${source === 'system' ? 'bg-plum/10 text-plum' : 'bg-ember/10 text-ember'}`}
    >
      {source}
    </span>
  );
}

// ── Profile Stats Helper ───────────────────────────────────────────────────

function computeProfileStats(
  profile: RoomProfile,
  inventoryItems: { id: string; moveUnits?: number; weightInPounds?: number; cubicFeet?: number }[],
) {
  let totalMoveUnits = 0;
  let totalWeight = 0;
  let totalCubicFeet = 0;

  for (const pi of profile.items) {
    const inv = inventoryItems.find(i => i.id === pi.itemId);
    if (inv) {
      totalMoveUnits += (inv.moveUnits ?? 0) * pi.quantity;
      totalWeight += (inv.weightInPounds ?? 0) * pi.quantity;
      totalCubicFeet += (inv.cubicFeet ?? 0) * pi.quantity;
    }
  }

  return { totalMoveUnits, totalWeight, totalCubicFeet };
}

// ── Room Card ──────────────────────────────────────────────────────────────

interface RoomCardProps {
  room: OrgResidentialRoom;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  inventoryItems: { id: string; name: string; moveUnits?: number; weightInPounds?: number; cubicFeet?: number }[];
}

function RoomCard({ room, expanded, onToggle, onEdit, onDelete, inventoryItems }: RoomCardProps) {
  const profileCount = room.profiles?.length ?? 0;
  const enabledProfiles = room.profiles?.filter(p => p.items.length > 0) ?? [];

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      {/* Room header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left
                   hover:bg-mist/50 active:bg-mist transition-colors duration-100"
      >
        <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
          <Home size={18} className="text-plum" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">{room.name}</p>
            <SourceBadge source={room.source} />
          </div>
          <p className="text-[13px] text-slate mt-0.5 leading-snug">
            {profileCount} profile{profileCount !== 1 ? 's' : ''} configured
            {enabledProfiles.length > 0 && (
              <span> · {enabledProfiles.map(p => LEVEL_LABELS[p.level]).join(', ')}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={onEdit}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
            aria-label={`Edit ${room.name}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-rose hover:text-rose active:bg-mist
                       transition-colors duration-150"
            aria-label={`Delete ${room.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
        <ChevronRight
          size={16}
          className={`shrink-0 text-slate transition-transform duration-150
            ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {/* Expanded profiles */}
      {expanded && (
        <div className="border-t border-stone px-4 py-3 flex flex-col gap-3">
          {PROFILE_LEVELS.map(level => {
            const profile = room.profiles?.find(p => p.level === level);
            const hasItems = profile && profile.items.length > 0;
            const stats = profile ? computeProfileStats(profile, inventoryItems) : null;

            return (
              <div key={level} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${hasItems ? 'bg-plum' : 'bg-stone'}`}
                  />
                  <p className={`text-[13px] font-semibold ${hasItems ? 'text-midnight' : 'text-slate'}`}>
                    {LEVEL_LABELS[level]}
                  </p>
                  {!hasItems && (
                    <span className="text-[11px] text-slate">— empty</span>
                  )}
                  {hasItems && stats && (
                    <span className="text-[11px] text-slate ml-auto">
                      {stats.totalMoveUnits > 0 && `${stats.totalMoveUnits} MU`}
                      {stats.totalWeight > 0 && `${stats.totalMoveUnits > 0 ? ' · ' : ''}${stats.totalWeight} lbs`}
                      {stats.totalCubicFeet > 0 && `${(stats.totalMoveUnits > 0 || stats.totalWeight > 0) ? ' · ' : ''}${stats.totalCubicFeet} cu ft`}
                    </span>
                  )}
                </div>
                {hasItems && (
                  <div className="ml-4 flex flex-col gap-0.5">
                    {profile.averageSquareFeet != null && (
                      <p className="text-[11px] text-slate">~{profile.averageSquareFeet} sq ft avg</p>
                    )}
                    {profile.items.map((item, idx) => (
                      <p key={idx} className="text-[12px] text-slate/80">
                        {item.itemName} ×{item.quantity}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Smart Room Profiles Tab ───────────────────────────────────────────────

const SMART_LEVELS: SmartProfileLevel[] = ['minimal', 'standard', 'heavy', 'full'];

const SMART_LEVEL_LABELS: Record<SmartProfileLevel, string> = {
  minimal: 'Minimal',
  standard: 'Standard',
  heavy: 'Heavy',
  full: 'Full',
};

interface SmartCardGroup {
  label: string;
  propertyTypes: ResidentialPropertyType[];
}

const SMART_CARD_GROUPS: SmartCardGroup[] = [
  { label: 'Single Family / Townhouse / Multi-Family', propertyTypes: ['single_family', 'townhouse', 'multi_family'] },
  { label: 'Condo / Apartment', propertyTypes: ['condo', 'apartment'] },
];

function OrgSmartRoomProfilesTab({ orgId }: { orgId: string }) {
  const { configs, loading, error, saveConfigs, syncFromMaster } = useOrgSmartRoomProfiles(orgId);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<SmartRoomProfileConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');

  function enterEditMode() {
    setDraft(configs.map(c => ({
      ...c,
      thresholds: c.thresholds.map(t => ({ ...t })),
    })));
    setEditMode(true);
    setSaveError('');
  }

  function updateThreshold(propertyType: ResidentialPropertyType, level: SmartProfileLevel, maxSqft: number | undefined) {
    setDraft(prev => prev.map(c => {
      if (c.propertyType !== propertyType) return c;
      return {
        ...c,
        thresholds: c.thresholds.map(t =>
          t.level === level ? { ...t, maxSqft } : t,
        ),
      };
    }));
  }

  function getConfigForType(pt: ResidentialPropertyType): SmartRoomProfileConfig {
    const source = editMode ? draft : configs;
    return source.find(c => c.propertyType === pt) ?? {
      propertyType: pt,
      thresholds: SMART_LEVELS.map(l => ({ level: l })),
    };
  }

  function getGroupConfig(group: SmartCardGroup): SmartRoomProfileConfig {
    return getConfigForType(group.propertyTypes[0]);
  }

  function updateGroupThreshold(group: SmartCardGroup, level: SmartProfileLevel, maxSqft: number | undefined) {
    for (const pt of group.propertyTypes) {
      updateThreshold(pt, level, maxSqft);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      await saveConfigs(draft);
      setEditMode(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    setRestoring(true);
    setRestoreError('');
    try {
      await syncFromMaster();
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoring(false);
      setShowRestoreConfirm(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Failed to load smart profiles</p>
        <p className="text-small text-slate">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13px] text-slate leading-snug max-w-xl">
          Define which room profile (Minimal, Standard, Heavy, Full) is automatically assigned to
          residential quotes based on property type and square footage. Each profile threshold sets the
          maximum square footage — anything above the Heavy threshold uses Full.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {!editMode && (
            <button
              type="button"
              onClick={() => setShowRestoreConfirm(true)}
              disabled={restoring}
              className="h-9 px-4 rounded-xl border border-stone bg-white
                         text-[13px] font-semibold text-slate
                         hover:border-plum hover:text-plum active:bg-mist
                         disabled:opacity-50
                         transition-colors duration-150 flex items-center gap-1.5"
            >
              <RefreshCw size={13} className={restoring ? 'animate-spin' : ''} />
              Restore
            </button>
          )}
          {!editMode ? (
            <button
              type="button"
              onClick={enterEditMode}
              className="h-9 px-4 rounded-xl border border-stone bg-white
                         text-[13px] font-semibold text-midnight
                         hover:border-plum hover:text-plum active:bg-mist
                         transition-colors duration-150 flex items-center gap-1.5"
            >
              <Pencil size={13} />
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="h-9 px-4 rounded-xl border border-stone bg-white
                           text-[13px] font-semibold text-slate
                           hover:text-midnight active:bg-mist
                           transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="h-9 px-4 rounded-xl bg-plum text-white
                           text-[13px] font-semibold
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150 flex items-center gap-1.5"
              >
                {saving ? <LoadingSpinner size="sm" /> : <Check size={13} />}
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      {restoreError && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{restoreError}</p>
          <button
            type="button"
            onClick={() => setRestoreError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {SMART_CARD_GROUPS.map(group => {
        const config = getGroupConfig(group);

        return (
          <div key={group.label} className="bg-white border border-stone rounded-xl shadow-brand-sm p-4">
            <h3 className="font-semibold text-small text-midnight mb-3">{group.label}</h3>
            <div className="flex flex-col gap-2.5">
              {SMART_LEVELS.map(level => {
                const threshold = config.thresholds.find(t => t.level === level);
                const isFull = level === 'full';
                const heavyThreshold = config.thresholds.find(t => t.level === 'heavy');

                return (
                  <div key={level} className="flex items-center gap-3">
                    <p className="text-[13px] font-semibold text-midnight w-20 shrink-0">
                      {SMART_LEVEL_LABELS[level]}
                    </p>
                    {isFull ? (
                      <p className="text-[13px] text-slate flex-1">
                        {heavyThreshold?.maxSqft
                          ? `Above ${heavyThreshold.maxSqft.toLocaleString()} sq ft`
                          : 'Everything else (above Heavy threshold)'}
                      </p>
                    ) : editMode ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-[12px] text-slate">Under</span>
                        <input
                          type="number"
                          min={0}
                          value={threshold?.maxSqft ?? ''}
                          onChange={e =>
                            updateGroupThreshold(
                              group,
                              level,
                              e.target.value ? Number(e.target.value) : undefined,
                            )
                          }
                          placeholder="sq ft"
                          className="w-28 h-9 px-3 rounded-lg border border-stone bg-white
                                     text-[13px] text-midnight text-center
                                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                     transition-colors duration-150"
                        />
                        <span className="text-[12px] text-slate">sq ft</span>
                      </div>
                    ) : (
                      <p className="text-[13px] text-slate flex-1">
                        {threshold?.maxSqft
                          ? `Under ${threshold.maxSqft.toLocaleString()} sq ft`
                          : <span className="text-slate/50">Not configured</span>}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Restore confirm */}
      <ConfirmModal
        isOpen={showRestoreConfirm}
        title="Restore from Master"
        message="This will replace your organization's smart profile thresholds with the master-level configuration. Any customizations will be lost."
        confirmLabel="Restore"
        variant="primary"
        onConfirm={handleRestore}
        onClose={() => setShowRestoreConfirm(false)}
      />
    </div>
  );
}

// ── Main Page Content ──────────────────────────────────────────────────────

type OrgResidentialTab = 'inventories' | 'auto-add' | 'smart-profiles';

function ResidentialDefaultsContent() {
  const navigate = useNavigate();
  const { adminOrg } = useUserProfile();
  const orgId = adminOrg?.orgId ?? '';

  const { rooms, loading, error, createRoom, updateRoom, deleteRoom, syncFromMaster, reorderRooms } =
    useOrgResidentialRooms(orgId);
  const { items: inventoryItems } = useOrgInventory(orgId);

  const [activeTab, setActiveTab] = useState<OrgResidentialTab>('inventories');
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<OrgResidentialRoom | null>(null);
  const [deletingRoom, setDeletingRoom] = useState<OrgResidentialRoom | null>(null);
  const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState<{ added: number; updated: number } | null>(null);
  const [restoreError, setRestoreError] = useState('');

  // Reorder mode (drag-and-drop)
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<OrgResidentialRoom[]>([]);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const filtered = rooms.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()),
  );

  function enterReorderMode() {
    setReorderList([...rooms]);
    setReorderMode(true);
  }

  function handleDragStart(e: React.DragEvent, id: string) {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) setDragOverId(id);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const sourceId = draggedId ?? e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    setReorderList(prev => {
      const result = [...prev];
      const srcIdx = result.findIndex(r => r.id === sourceId);
      const tgtIdx = result.findIndex(r => r.id === targetId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const [moved] = result.splice(srcIdx, 1);
      result.splice(tgtIdx, 0, moved);
      return result;
    });
    setDraggedId(null);
    setDragOverId(null);
  }

  function handleDragEnd() {
    setDraggedId(null);
    setDragOverId(null);
  }

  async function saveReorder() {
    setReorderSaving(true);
    try {
      const order = reorderList.map((r, i) => ({ id: r.id, sortOrder: i }));
      await reorderRooms(order);
      setReorderMode(false);
    } catch {
      // stay in reorder mode on error
    } finally {
      setReorderSaving(false);
    }
  }

  async function handleRestore() {
    setRestoring(true);
    setRestoreError('');
    setRestoreResult(null);
    try {
      const result = await syncFromMaster();
      setRestoreResult(result);
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRestoring(false);
      setShowRestoreConfirm(false);
    }
  }

  if (!orgId) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">No organization selected.</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-5xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/quoting')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Quoting
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Residential Rooms</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Residential Defaults</h1>
      <p className="text-[13px] text-slate mb-4 leading-snug">
        Default rooms and profiles for residential moves in your organization.
      </p>

      {/* Tabs */}
      <div className="flex bg-mist rounded-xl p-1 gap-1 mb-5">
        {([
          { key: 'inventories' as OrgResidentialTab, label: 'Room Inventories' },
          { key: 'auto-add' as OrgResidentialTab, label: 'Auto-Add Rules' },
          { key: 'smart-profiles' as OrgResidentialTab, label: 'Smart Profiles' },
        ]).map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 text-[13px] font-semibold rounded-lg transition-all duration-150
              ${activeTab === tab.key
                ? 'bg-white text-midnight shadow-brand-sm'
                : 'text-slate hover:text-midnight'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Auto-Add Rules Tab */}
      {activeTab === 'auto-add' && <OrgAutoAddRulesTab rooms={rooms} orgId={orgId} />}

      {/* Smart Profiles Tab */}
      {activeTab === 'smart-profiles' && <OrgSmartRoomProfilesTab orgId={orgId} />}

      {/* Room Inventories Tab — everything below is existing inventory content */}
      {activeTab !== 'inventories' ? null : (<>

      {/* Banners */}
      {restoreResult && (
        <div className="mb-4">
          <RestoreResultBanner {...restoreResult} onClose={() => setRestoreResult(null)} />
        </div>
      )}
      {restoreError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-rose">{restoreError}</p>
          </div>
          <button
            type="button"
            onClick={() => setRestoreError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Toolbar */}
      {!reorderMode ? (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none"
            />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search rooms..."
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
          {rooms.length > 1 && (
            <button
              type="button"
              onClick={enterReorderMode}
              className="shrink-0 h-11 px-4 rounded-xl border border-stone bg-white
                         flex items-center justify-center gap-2
                         text-small font-semibold text-midnight
                         hover:border-plum hover:text-plum active:bg-mist
                         transition-colors duration-150"
            >
              <GripVertical size={14} />
              Reorder
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowRestoreConfirm(true)}
            disabled={restoring}
            className="shrink-0 h-11 px-4 rounded-xl border border-stone bg-white
                       flex items-center gap-2
                       text-small font-semibold text-slate
                       hover:border-plum hover:text-plum active:bg-mist
                       disabled:opacity-50
                       transition-colors duration-150"
          >
            <RefreshCw size={15} className={restoring ? 'animate-spin' : ''} />
            Restore
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="shrink-0 h-11 px-4 rounded-xl bg-plum text-white
                       flex items-center justify-center gap-2
                       text-small font-semibold
                       hover:bg-rose active:opacity-80
                       transition-colors duration-150"
          >
            <Plus size={16} />
            Add Room
          </button>
        </div>
      ) : (
        /* Reorder toolbar */
        <div className="flex items-center gap-2 mb-5">
          <p className="text-small font-semibold text-midnight flex-1">
            Drag rooms to reorder
          </p>
          <button
            type="button"
            onClick={() => setReorderMode(false)}
            className="h-11 px-4 rounded-xl border border-stone bg-white
                       text-small font-semibold text-slate
                       hover:text-midnight active:bg-mist
                       transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveReorder}
            disabled={reorderSaving}
            className="h-11 px-4 rounded-xl bg-plum text-white
                       text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 flex items-center gap-2"
          >
            {reorderSaving ? <LoadingSpinner size="sm" /> : <Check size={14} />}
            Save Order
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load rooms</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && rooms.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
            <Home size={24} className="text-slate" />
          </div>
          <p className="text-small text-slate">
            No rooms yet. Add one or restore from master defaults.
          </p>
        </div>
      )}

      {/* No search results */}
      {!loading && !error && rooms.length > 0 && filtered.length === 0 && !reorderMode && (
        <div className="flex flex-col items-center py-12 gap-2 text-center">
          <p className="text-small text-slate">No rooms match your search.</p>
        </div>
      )}

      {/* Reorder list (drag-and-drop) */}
      {reorderMode && reorderList.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
            Rooms <span className="font-normal normal-case tracking-normal ml-1">({reorderList.length})</span>
          </p>
          <ul className="flex flex-col gap-2">
            {reorderList.map((room, index) => (
              <li key={room.id}>
                <div
                  draggable
                  onDragStart={e => handleDragStart(e, room.id)}
                  onDragOver={e => handleDragOver(e, room.id)}
                  onDrop={e => handleDrop(e, room.id)}
                  onDragEnd={handleDragEnd}
                  className={`bg-white border rounded-xl p-4 shadow-brand-sm flex items-center gap-3
                    select-none transition-all duration-100
                    ${dragOverId === room.id ? 'border-plum border-2' : 'border-stone'}
                    ${draggedId === room.id ? 'opacity-40' : 'opacity-100'}`}
                >
                  <div className="shrink-0 text-slate hover:text-midnight cursor-grab active:cursor-grabbing">
                    <GripVertical size={18} />
                  </div>
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
                    <Home size={18} className="text-plum" />
                  </div>
                  <p className="font-semibold text-small text-midnight flex-1 min-w-0">{room.name}</p>
                  <span className="text-[12px] text-slate shrink-0">#{index + 1}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Rooms list (normal mode) */}
      {!loading && !error && !reorderMode && filtered.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
            Rooms
            <span className="font-normal normal-case tracking-normal ml-1">
              ({filtered.length})
            </span>
            {search && (
              <span className="font-normal normal-case tracking-normal">
                {' '}matching "{search}"
              </span>
            )}
          </p>

          <ul className="flex flex-col gap-2">
            {filtered.map(room => (
              <li key={room.id}>
                <RoomCard
                  room={room}
                  expanded={expandedRoomId === room.id}
                  onToggle={() => setExpandedRoomId(prev => (prev === room.id ? null : room.id))}
                  onEdit={() => setEditingRoom(room)}
                  onDelete={() => setDeletingRoom(room)}
                  inventoryItems={inventoryItems}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Add form */}
      {showAddForm && (
        <RoomForm
          orgId={orgId}
          onClose={() => setShowAddForm(false)}
          onSave={createRoom}
        />
      )}

      {/* Edit form */}
      {editingRoom && (
        <RoomForm
          orgId={orgId}
          initial={editingRoom}
          onClose={() => setEditingRoom(null)}
          onSave={input => updateRoom(editingRoom.id, input)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deletingRoom}
        title="Delete Room"
        message={`Are you sure you want to delete "${deletingRoom?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deletingRoom) deleteRoom(deletingRoom.id);
          setDeletingRoom(null);
        }}
        onClose={() => setDeletingRoom(null)}
      />

      {/* Restore confirm (room inventories) */}
      <ConfirmModal
        isOpen={showRestoreConfirm}
        title="Restore from Master"
        message="This will sync your rooms with the master defaults. New rooms will be added and existing system rooms will be updated. Your custom rooms will not be affected."
        confirmLabel="Restore"
        variant="primary"
        onConfirm={handleRestore}
        onClose={() => setShowRestoreConfirm(false)}
      />

      </>)}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function ResidentialDefaultsPage() {
  const { loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return <ResidentialDefaultsContent />;
}
